<script setup lang="ts">
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import { useCopilotFeedbackStore } from "~/store/copilot/copilot-feedback.store";
import { CopilotFeedbackEnum } from "~/utils/enums/copilot/chat.enums";

const props = defineProps<{
  /** Conversation the message belongs to — required by the feedback endpoint. */
  chatId: string;
  messageId: string;
  /** Id of the generation behind the message, so the rating can be tied to it backend-side. */
  responseId?: string;
  disabled?: boolean;
}>();

const copilotFeedbackStore = useCopilotFeedbackStore();

const THUMBS: { vote: CopilotFeedbackEnum; icon: string; tooltip: string }[] = [
  { vote: CopilotFeedbackEnum.UP, icon: "thumb_up", tooltip: "Réponse utile" },
  { vote: CopilotFeedbackEnum.DOWN, icon: "thumb_down", tooltip: "Réponse à côté" },
];

const draftComment = ref("");

const currentVote = computed(() => copilotFeedbackStore.voteOf(props.messageId));
const isCommentOpen = computed(() => copilotFeedbackStore.isCommentOpen(props.messageId));

function onVote(vote: CopilotFeedbackEnum) {
  draftComment.value = "";
  copilotFeedbackStore.vote(props.chatId, props.messageId, vote, props.responseId);
}

function onSendComment() {
  if (!draftComment.value.trim()) return;

  copilotFeedbackStore.comment(props.chatId, props.messageId, draftComment.value.trim(), props.responseId);
  draftComment.value = "";
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-1">
      <Tooltip v-for="thumb of THUMBS" :key="thumb.vote" position="top-mid" minified>
        <template #img>
          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            :class="
              currentVote === thumb.vote
                ? 'bg-light-blue-color text-primary-blue-color'
                : 'text-secondary-color hover:bg-lightest-grey-color hover:text-primary-color'
            "
            :disabled="disabled"
            :aria-pressed="currentVote === thumb.vote"
            :aria-label="thumb.tooltip"
            @click="onVote(thumb.vote)"
          >
            <SvgIcon :name="thumb.icon" class="h-[15px] w-[15px]" />
          </button>
        </template>
        <template #content>
          <span class="meta whitespace-nowrap">{{ thumb.tooltip }}</span>
        </template>
      </Tooltip>

      <span v-if="currentVote && !isCommentOpen" class="meta text-secondary-color">Merci pour le retour</span>
    </div>

    <div v-if="isCommentOpen" class="flex flex-col gap-2 rounded-xl border border-stroke-color bg-light-color p-3">
      <span class="small_demi text-secondary-color">Qu'est-ce qui n'allait pas ?</span>
      <textarea
        v-model="draftComment"
        class="w-full resize-none rounded-lg border border-stroke-color bg-lightest-grey-color px-3 py-2 text-primary-color meta_regular placeholder:text-grey-medium-color focus:border-primary-blue-color focus:outline-none"
        placeholder="Décrivez le problème…"
        rows="2"
        maxlength="2000"
      />
      <div class="flex justify-end gap-2">
        <AppButton type="link" label="Passer" @click="copilotFeedbackStore.closeComment()" />
        <AppButton label="Envoyer" :disabled="!draftComment.trim()" @click="onSendComment()" />
      </div>
    </div>
  </div>
</template>
